import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { CommentFormData } from './types';

interface CommentFormProps {
  readonly data: CommentFormData | null;
  readonly mode: 'add' | 'edit';
  readonly onCancel: () => void;
  readonly onSave?: (data: CommentFormData) => void;
}

/**
 * Comment form component for adding/editing comments.
 * Ready for react-hook-form integration.
 */
export function CommentForm({ data, mode, onCancel, onSave }: CommentFormProps) {
  const [text, setText] = useState('');

  useEffect(() => {
    setText(data?.text ?? '');
  }, [data]);

  const isAddMode = mode === 'add';
  const title = isAddMode ? 'Add Comment' : 'Edit Comment';

  const handleSave = () => {
    if (onSave) {
      onSave({
        ...data,
        text,
      });
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography id="comment-modal-title" variant="h6">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isAddMode ? 'Add a new comment to this record.' : 'Update your comment.'}
        </Typography>
      </Box>

      <TextField
        label="Comment"
        value={text}
        onChange={(event) => setText(event.target.value)}
        fullWidth
        multiline
        rows={4}
        placeholder="Enter your comment..."
      />

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!text.trim()}>
          {isAddMode ? 'Add' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default CommentForm;
